import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';
import type { GetContextMenuItems } from 'ag-grid-community';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { Maximize2, Minimize2, RefreshCw, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { WithLocalStorage } from '@/components';
import type { GradingGridRow } from '@/components/common';
import { namespacedKey } from '@/components/hooks/useLocalStorage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, lookupSideBar } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { useAccessToken, useCurrentUser } from '@/services/auth';
import { PrimaryContext } from '@/services/context';
import {
  onFarmImplantFreshEmbryosOptions,
  onFarmImplantFreshMatingsOptions,
  type OnFarmImplantRecordKey,
  toRecordKey,
  useSaveOnFarmImplantFreshDegenerates,
} from '@/services/on-farm-implant';
import { useContextMenu } from '@/utils/context-menu';

import { getMatingBalance } from './balance';
import { type FreshMatingRow, useFreshMatingColumnDefs } from './columnDefs';
import { FreshMatingDetail } from './FreshMatingDetail';
import {
  FreshTabContext,
  type FreshTabContextValue,
  type GradeFilter,
} from './FreshTabContext';

type FreshTabProps = {
  sessionId: string | null;
  records: OnFarmImplantRecordKey[];
  readOnly?: boolean;
};

type FreshDraft = {
  nextStrawNumber: string;
  gradingByMating: Record<string, GradingGridRow[]>;
  gradeFilterByMating: Record<string, GradeFilter>;
};

const draftStorageKey = (sessionId: string) =>
  `on-farm-implant-fresh-draft:${sessionId}`;

const readDraft = (sessionId: string | null): FreshDraft => {
  const empty: FreshDraft = {
    nextStrawNumber: '',
    gradingByMating: {},
    gradeFilterByMating: {},
  };
  if (!sessionId) return empty;
  try {
    const stored = sessionStorage.getItem(draftStorageKey(sessionId));
    return stored
      ? { ...empty, ...(JSON.parse(stored) as Partial<FreshDraft>) }
      : empty;
  } catch {
    return empty;
  }
};

type AutoSettings = { grade2: boolean; grade3: boolean; degenerates: boolean };

const noAutoSettings: AutoSettings = {
  grade2: false,
  grade3: false,
  degenerates: false,
};

const readAutoSettings = (storageKey: string): AutoSettings => {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored
      ? {
          ...noAutoSettings,
          ...(JSON.parse(stored) as Partial<AutoSettings>),
        }
      : noAutoSettings;
  } catch {
    return noAutoSettings;
  }
};

const useAutoSettings = (userId: string | undefined) => {
  const storageKey = namespacedKey(
    `on-farm-implant-auto-discard:${userId ?? 'default'}`,
  );
  const [state, setState] = useState(() => ({
    storageKey,
    settings: readAutoSettings(storageKey),
  }));
  const settings =
    state.storageKey === storageKey
      ? state.settings
      : readAutoSettings(storageKey);

  const update = useCallback(
    (next: AutoSettings) => {
      localStorage.setItem(storageKey, JSON.stringify(next));
      setState({ storageKey, settings: next });
    },
    [storageKey],
  );

  return [settings, update] as const;
};

export const FreshTab = ({
  sessionId,
  records,
  readOnly = false,
}: FreshTabProps) => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const gridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const gridRef = useRef<AgGridReact>(null);
  const columnDefs = useFreshMatingColumnDefs();
  const { getContextMenuItems: getMatingMenuItems } = useContextMenu({
    idKey: 'id',
  });
  const getContextMenuItems = useCallback<GetContextMenuItems>(
    (params) => (params.node?.detail ? [] : getMatingMenuItems(params)),
    [getMatingMenuItems],
  );
  const [draft, setDraft] = useState(() => readDraft(sessionId));
  const [searchText, setSearchText] = useState('');
  const sideBar = useMemo(() => {
    const base = lookupSideBar('on-farm-implant-fresh');
    return {
      ...base,
      toolPanels: base.toolPanels?.map((panel) =>
        typeof panel === 'string' || panel.id !== 'customViews'
          ? panel
          : {
              ...panel,
              toolPanelParams: {
                ...panel.toolPanelParams,
                onClearAllFilters: () => setSearchText(''),
              },
            },
      ),
    };
  }, []);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const { data: currentUser } = useCurrentUser();
  const [autoSettings, setAutoSettings] = useAutoSettings(currentUser?.userId);
  const hasWork = !!sessionId && records.length > 0;
  const {
    data: matings = [],
    isLoading,
    isFetching,
    refetch: refetchMatings,
  } = useQuery({
    ...onFarmImplantFreshMatingsOptions(token, sessionId ?? ''),
    enabled: !!token && hasWork,
  });
  const { data: embryos = [], refetch: refetchEmbryos } = useQuery({
    ...onFarmImplantFreshEmbryosOptions(token, sessionId ?? ''),
    enabled: !!token && hasWork,
  });
  const { mutateAsync: saveDegenerates } =
    useSaveOnFarmImplantFreshDegenerates();
  const pendingDegenerates = useRef(new Set<string>());

  useEffect(() => {
    if (!autoSettings.degenerates || readOnly || !sessionId) return;
    // Allocation is stored per mating and shared by every account row.
    const handled = new Set<string>();
    for (const mating of matings) {
      if (handled.has(mating.id)) continue;
      handled.add(mating.id);
      const balance = getMatingBalance(mating.allocation);
      if (!balance?.hasCreatedOrDiscarded) continue;
      const degenerates = Math.max(0, balance.calculatedDegenerates);
      const saveKey = `${mating.id}:${degenerates}`;
      if (
        mating.allocation?.onFarmDegenerates === degenerates ||
        pendingDegenerates.current.has(saveKey)
      )
        continue;
      pendingDegenerates.current.add(saveKey);
      saveDegenerates({
        sessionId,
        matingId: mating.id,
        degenerates,
        version: mating.allocation?.version,
      })
        .catch(() =>
          toast.add({
            data: { variant: 'error' },
            title: t('onFarmImplant.fresh.degenerates.autoFailed', {
              mating: mating.recordName ?? mating.id,
            }),
          }),
        )
        .finally(() => pendingDegenerates.current.delete(saveKey));
    }
  }, [
    autoSettings.degenerates,
    readOnly,
    sessionId,
    matings,
    saveDegenerates,
    t,
  ]);

  useEffect(() => {
    if (!sessionId) return;
    sessionStorage.setItem(draftStorageKey(sessionId), JSON.stringify(draft));
  }, [sessionId, draft]);

  const setNextStrawNumber = useCallback(
    (value: string) =>
      setDraft((current) => ({ ...current, nextStrawNumber: value })),
    [],
  );

  const setMatingGrading = useCallback(
    (matingKey: string, gradingRows: GradingGridRow[]) =>
      setDraft((current) => ({
        ...current,
        gradingByMating: {
          ...current.gradingByMating,
          [matingKey]: gradingRows,
        },
      })),
    [],
  );

  const { nextStrawNumber, gradingByMating, gradeFilterByMating } = draft;

  const setMatingGradeFilter = useCallback(
    (matingKey: string, filter: GradeFilter) =>
      setDraft((current) => ({
        ...current,
        gradeFilterByMating: {
          ...current.gradeFilterByMating,
          [matingKey]: filter,
        },
      })),
    [],
  );

  const suggestedNextStraw = useMemo(() => {
    const strawNumbers = embryos
      .map((embryo) => embryo.strawNumber)
      .filter((straw): straw is number => straw !== null);
    return strawNumbers.length > 0 ? String(Math.max(...strawNumbers) + 1) : '';
  }, [embryos]);
  const [filledSuggestion, setFilledSuggestion] = useState('');
  if (
    suggestedNextStraw &&
    filledSuggestion !== suggestedNextStraw &&
    (!nextStrawNumber || Number(nextStrawNumber) < Number(suggestedNextStraw))
  ) {
    setFilledSuggestion(suggestedNextStraw);
    setNextStrawNumber(suggestedNextStraw);
  }

  const rows = useMemo<FreshMatingRow[]>(
    () =>
      matings.map((mating) => {
        const matingEmbryos = embryos.filter(
          (embryo) =>
            embryo.matingId === mating.id &&
            (embryo.accountId ?? null) === (mating.accountId ?? null),
        );
        return {
          ...mating,
          createdCount: matingEmbryos.length,
          queuedCount: matingEmbryos.filter(
            (embryo) => embryo.queueState === 'Implanted',
          ).length,
          discardedCount: matingEmbryos.filter(
            (embryo) => embryo.queueState === 'Discarded',
          ).length,
        };
      }),
    [matings, embryos],
  );

  const contextValue = useMemo<FreshTabContextValue>(
    () => ({
      sessionId: sessionId ?? '',
      readOnly,
      embryos,
      nextStrawNumber,
      setNextStrawNumber,
      gradingByMating,
      setMatingGrading,
      gradeFilterByMating,
      setMatingGradeFilter,
      autoDiscardGrade2: autoSettings.grade2,
      autoDiscardGrade3: autoSettings.grade3,
      autoCalculateDegenerates: autoSettings.degenerates,
    }),
    [
      sessionId,
      readOnly,
      embryos,
      nextStrawNumber,
      setNextStrawNumber,
      gradingByMating,
      setMatingGrading,
      gradeFilterByMating,
      setMatingGradeFilter,
      autoSettings.grade2,
      autoSettings.grade3,
      autoSettings.degenerates,
    ],
  );

  const handleRefresh = useCallback(() => {
    void refetchMatings();
    void refetchEmbryos();
  }, [refetchMatings, refetchEmbryos]);

  const handleExpandCollapse = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (isExpanded) {
      api.forEachNode((node) => {
        if (node.master) node.setExpanded(false);
      });
      setIsExpanded(false);
      return;
    }
    const selected = api.getSelectedNodes();
    if (selected.length > 0) {
      selected.forEach((node) => node.setExpanded(true));
    } else {
      api.forEachNode((node) => {
        if (node.master) node.setExpanded(true);
      });
    }
    setIsExpanded(true);
  }, [isExpanded]);

  if (records.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-500">
        <i className="pi pi-inbox mb-3 text-3xl" />
        <h1 className="text-base font-semibold text-gray-800">
          {t('onFarmImplant.fresh.noWorkTitle')}
        </h1>
        <p className="mt-1 text-sm">{t('onFarmImplant.fresh.noWorkBody')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 py-24 text-sm text-gray-500">
        <i className="pi pi-spin pi-spinner" />
        {t('onFarmImplant.fresh.loading')}
      </div>
    );
  }

  return (
    <FreshTabContext.Provider value={contextValue}>
      <div className="flex flex-1 flex-col gap-1.5 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
            <Input
              placeholder={t('onFarmImplant.fresh.searchMatings')}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              className="min-w-44 pl-8"
            />
          </div>
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            {t('onFarmImplant.fresh.loadedCount', { count: rows.length })}
          </span>
          <div aria-hidden className="bg-border mx-1 h-4 w-px" />
          <label
            htmlFor="next-straw-number"
            className="text-muted-foreground text-xs font-medium whitespace-nowrap"
          >
            {t('onFarmImplant.fresh.nextStrawNumber')}
          </label>
          <Input
            id="next-straw-number"
            type="text"
            inputMode="numeric"
            disabled={readOnly}
            value={nextStrawNumber}
            onChange={(event) =>
              setNextStrawNumber(event.target.value.replace(/\D/g, ''))
            }
            className="w-32"
          />
          <div aria-hidden className="bg-border mx-1 h-4 w-px" />
          <div
            role="group"
            aria-label={t('onFarmImplant.fresh.autoDiscard')}
            className="text-muted-foreground flex items-center gap-3 text-xs font-medium whitespace-nowrap"
          >
            <span>{t('onFarmImplant.fresh.autoDiscard')}</span>
            {([2, 3] as const).map((grade) => {
              const key = grade === 2 ? 'grade2' : 'grade3';
              return (
                <label key={grade} className="flex items-center gap-1.5">
                  <Switch
                    size="sm"
                    aria-label={t('onFarmImplant.fresh.autoDiscardGrade', {
                      grade,
                    })}
                    checked={autoSettings[key]}
                    disabled={readOnly}
                    onCheckedChange={(checked) =>
                      setAutoSettings({ ...autoSettings, [key]: checked })
                    }
                  />
                  {t('onFarmImplant.fresh.grade', { grade })}
                </label>
              );
            })}
          </div>
          <div aria-hidden className="bg-border mx-1 h-4 w-px" />
          <div
            role="group"
            aria-label={t('onFarmImplant.fresh.autoCalculate')}
            className="text-muted-foreground flex items-center gap-3 text-xs font-medium whitespace-nowrap"
          >
            <span>{t('onFarmImplant.fresh.autoCalculate')}</span>
            <label className="flex items-center gap-1.5">
              <Switch
                size="sm"
                aria-label={t('onFarmImplant.fresh.autoCalculateDegenerates')}
                checked={autoSettings.degenerates}
                disabled={readOnly}
                onCheckedChange={(checked) =>
                  setAutoSettings({ ...autoSettings, degenerates: checked })
                }
              />
              {t('onFarmImplant.fresh.degenerates.label')}
            </label>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={isFetching}
            >
              <RefreshCw
                className={`size-3.5 ${isFetching ? 'animate-spin' : ''}`}
              />
              {t('onFarmImplant.fresh.refresh')}
            </Button>
            <Button variant="outline" onClick={handleExpandCollapse}>
              {isExpanded ? (
                <Minimize2 className="size-3.5" />
              ) : (
                <Maximize2 className="size-3.5" />
              )}
              {isExpanded
                ? t('onFarmImplant.fresh.collapse')
                : selectedCount > 0
                  ? t('onFarmImplant.fresh.expandSelected', {
                      count: selectedCount,
                    })
                  : t('onFarmImplant.fresh.expand')}
            </Button>
          </div>
        </div>

        <div
          className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-[calc(100vh-18rem)] min-h-[400px] w-full`}
        >
          <WithLocalStorage storageKey="on-farm-implant-fresh-grid">
            <AgGridReact
              ref={gridRef}
              gridOptions={gridOptions}
              columnDefs={columnDefs as AgGridReactProps['columnDefs']}
              defaultColDef={defaultColDef}
              rowData={rows}
              rowSelection="multiple"
              suppressRowClickSelection
              onSelectionChanged={(event) =>
                setSelectedCount(event.api.getSelectedNodes().length)
              }
              quickFilterText={searchText}
              masterDetail
              reactiveCustomComponents
              isRowMaster={() => true}
              detailCellRenderer={FreshMatingDetail}
              detailRowAutoHeight
              rowHeight={40}
              animateRows={false}
              getContextMenuItems={getContextMenuItems}
              sideBar={sideBar}
              getRowId={getRowId<FreshMatingRow>((params) =>
                toRecordKey(params.data.id, params.data.accountId),
              )}
            />
          </WithLocalStorage>
        </div>
      </div>
    </FreshTabContext.Provider>
  );
};
